import React, { useCallback, useMemo, useState } from "react";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import styled from "@emotion/styled";
import { Chip } from "@mui/material";
import { useField } from "formik";
import TextSimple from "../../typography/text-simple/TextSimple";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import toast from "react-hot-toast";
interface ChipInputProps
  extends Omit<TextFieldProps<"outlined">, "variant"> {
  limit?: number;
  disabled?: boolean;
}

function ChipInput(props: ChipInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [field, meta, helpers] = useField({ name: props.name! });

  const removeComa = useCallback((word: string) => {
    return word.replace(/,/g, "");
  }, []);

  const chips: any[] = useMemo(() => {
    return field?.value || [];
  }, [field?.value]);

  const handleInputChange = (newInputValue: any) => {
    setInputValue(newInputValue.target.value);
  };

  const handleDelete = (chip: any) => {
    const newChips = chips.filter((c: any) => c !== chip);
    helpers.setValue(newChips);
  };

  const handleAdd = (e: any) => {
    if (e.key === "Enter" || e.key === ",") {
      if (inputValue?.length === 0) return;
      if (props.limit && meta.value.length >= props.limit) {
        toast.error(
          `La cantidad máxima de palabras claves permitidas es de ${props.limit}`
        );
        return;
      } else if (chips.includes(inputValue)) {
        setInputValue("");
        return;
      }
      helpers.setValue([...field?.value, removeComa(inputValue).trim()]);
      setInputValue("");
    }
  };

  const addChip = () => {
    if (inputValue?.length === 0) return;
    if (props.limit && meta.value.length >= props.limit) {
      toast.error(
        `La cantidad máxima de palabras claves permitidas es de ${props.limit}`
      );
      return;
    } else if (chips.includes(inputValue)) {
      setInputValue("");
      return;
    }
    helpers.setValue([...field?.value, removeComa(inputValue).trim()]);
    setInputValue("");
  };

  return (
    <Content>
      <ContentField>
        <CustomField
          label={props.label}
          variant="outlined"
          fullWidth
          value={inputValue}
          onChange={handleInputChange}
          onKeyUpCapture={handleAdd}
          disabled={props.disabled}
        />
        <ContentButton children={<AddOutlinedIcon />} onClick={addChip} />
      </ContentField>

      <ContentChips>
        {chips?.map((chip: any, index: number) => {
          return (
            <CustomTag
              key={index}
              label={chip}
              onDelete={() => handleDelete(chip)}
            />
          );
        })}
      </ContentChips>
      {meta.touched && meta.error && (
        <TextSimple color="red" weight="700" size="description">
          {meta.touched ? meta?.error : ""}
        </TextSimple>
      )}
    </Content>
  );
}

export default ChipInput;

const ContentField = styled.div`
  position: relative;
`;

const ContentButton = styled.div`
  position: absolute;
  right: 6px;
  top: 6px;
  color: white;
  font-size: var(--fs-text-small);
  height: 30px;
  width: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 100%;
  background-color: var(--color-blue-main);
  cursor: pointer;
  svg {
    width: 20px;
    height: 20px;
  }
`;

const CustomField = styled(TextField)`
  .MuiFormLabel-root {
    font-size: var(--fs-text-small) !important;
    color: var(--color-blue-main);
    &.Mui-focused {
      color: var(--color-blue-main);
    }
  }
  input.MuiInputBase-input.MuiOutlinedInput-input {
    height: 10px;
  }

  & .MuiFormHelperText-root {
    color: var(--m-color-red-01) !important;
    opacity: 0.8;
    font-size: 11px;
  }
  label {
    color: var(--m-color-blue-03);
    font-weight: 700;
    font-family: var(--ff-inter);
    font-size: var(--fs-placeholder);
  }
  & .MuiOutlinedInput-root {
    border-radius: 50px;
  }
`;

const CustomTag = styled(Chip)`
  margin-left: 2px;
  padding: 1px 0px;
  height: 25px;
  background-color: var(--color-blue-main);
  color: white;

  font-size: var(--fs-text-small);
  font-weight: 500;
  svg {
    width: 18px;
    height: 18px;
    color: white;
    fill: white;
  }
`;

const Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const ContentChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 10px;
`;
